import React, { useState, useEffect } from 'react';
import { AuthUser, User, CheckIn } from '../types';
import { signOutUser } from '../firebase/auth';
import { getAllUsers, getAllCheckIns, calculateAllUsersPTO } from '../firebase/firestore';
import AdminUsersList from './AdminUsersList';
import AdminWorkLocations from './AdminWorkLocations';
import AdminCheckInStatus from './AdminCheckInStatus';
import AdminCheckInHistory from './AdminCheckInHistory';
import AdminCreateUser from './AdminCreateUser';
import AdminAttendanceTimesheet from './AdminAttendanceTimesheet';
import AdminCalendarEvents from './AdminCalendarEvents';
import AdminPTOFormulas from './AdminPTOFormulas';

interface AdminDashboardProps {
  user: AuthUser;
}

type TabType = 'users' | 'locations' | 'checkins' | 'create' | 'history' | 'timesheet' | 'calendar' | 'pto-formulas';

const AdminDashboard: React.FC<AdminDashboardProps> = ({ user }) => {
  const [activeTab, setActiveTab] = useState<TabType>('timesheet');
  const [users, setUsers] = useState<User[]>([]);
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const [loading, setLoading] = useState(true);

  const loadAdminData = async () => {
    try {
      const [usersData, checkInsData] = await Promise.all([
        getAllUsers(),
        getAllCheckIns()
      ]);
      setUsers(usersData);
      setCheckIns(checkInsData);
      
      // Automatically calculate PTO for all users when admin logs in
      try {
        await calculateAllUsersPTO();
        console.log('PTO calculations completed for all users');
      } catch (ptoError) {
        console.error('Error calculating PTO for users:', ptoError);
        // Don't fail the entire load if PTO calculation fails
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdminData();
  }, []);

  const handleUserCreated = () => {
    // Refresh the data when a new user is created
    loadAdminData();
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Yönetici Paneli</h1>
              <p className="text-gray-600">Hoş geldiniz, {user.email}</p>
            </div>
            <button
              onClick={handleSignOut}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Çıkış Yap
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('checkins')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'checkins'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Bugünkü Girişler
                
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'users'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Kullanıcılar
               
              </button>
              <button
                onClick={() => setActiveTab('locations')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'locations'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                İş Yeri Konumları
              </button>
              <button
                onClick={() => setActiveTab('create')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'create'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Yeni Kullanıcı
                <span className="ml-2 bg-green-100 text-green-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                  +
                </span>
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'history'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Geçmiş Girişler
              </button>
              <button
                onClick={() => setActiveTab('timesheet')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'timesheet'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Devam Çizelgesi
              </button>
              <button
                onClick={() => setActiveTab('calendar')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'calendar'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Takvim Etkinlikleri
              </button>
              <button
                onClick={() => setActiveTab('pto-formulas')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'pto-formulas'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Yıllık İzin Formülleri
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="bg-white shadow rounded-lg">
            {activeTab === 'checkins' && (
              <AdminCheckInStatus users={users} checkIns={checkIns} />
            )}
            {activeTab === 'history' && (
              <AdminCheckInHistory users={users} checkIns={checkIns} />
            )}
            {activeTab === 'timesheet' && (
              <AdminAttendanceTimesheet users={users} checkIns={checkIns} currentUser={user} />
            )}
            {activeTab === 'users' && (
              <AdminUsersList users={users} onUserUpdate={loadAdminData} />
            )}
            {activeTab === 'locations' && (
              <AdminWorkLocations />
            )}
            {activeTab === 'create' && (
              <AdminCreateUser onUserCreated={handleUserCreated} />
            )}
            {activeTab === 'calendar' && (
              <AdminCalendarEvents users={users} currentUserId={user.uid} />
            )}
            {activeTab === 'pto-formulas' && (
              <AdminPTOFormulas currentUser={user} />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard; 